import { makeAutoObservable } from 'mobx';
import { createAudioBufferFromLPCM } from '@ai/common/src/utils/createAudioBufferFromLPCM';

interface AudioConfig {
  sampleRate: number;
  numChannels: number;
  onPlaybackEnded?: () => void;
  onStartPlaying?: () => void;
}

const FADEOUT_DURATION_SEC = 0.7;
const MIN_QUEUE_LENGTH = 0.5;

export class LpcmPlayer {
  private audioCtx: AudioContext;
  private activeSources: AudioBufferSourceNode[] = [];
  private bufferQueue: AudioBuffer[] = [];
  private gainNode: GainNode;
  private mediaStreamDest: MediaStreamAudioDestinationNode; // ← Новый узел
  public playbackStream: MediaStream; // ← Экспортируемый стрим для записи

  public isPlaying: boolean = false;

  public isAllChunksRecieved: boolean = false;
  private readonly config: AudioConfig;
  private nextPlayTime = 0.0;
  private queueDuration = 0.0;

  constructor(config: AudioConfig) {
    this.config = config;
    this.audioCtx = new (
      window.AudioContext ||
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (window as any).webkitAudioContext
    )({
      sampleRate: this.config.sampleRate,
    });
    this.gainNode = this.audioCtx.createGain();
    this.mediaStreamDest = this.audioCtx.createMediaStreamDestination();

    // Подключаем: gain → destination (запись) и gain → speakers (воспроизведение)
    this.gainNode.connect(this.mediaStreamDest);
    this.gainNode.connect(this.audioCtx.destination);

    this.playbackStream = this.mediaStreamDest.stream; // ← Готовый стрим для записи

    makeAutoObservable(this, {}, { autoBind: true });
  }

  setIsAllChunksRecieved = (value: boolean) => {
    this.isAllChunksRecieved = value;
  };

  enqueue(chunk: ArrayBuffer): void {
    const audioBuffer = createAudioBufferFromLPCM({
      arrayBuffer: chunk,
      numChannels: this.config.numChannels,
      sampleRate: this.config.sampleRate,
      context: this.audioCtx!,
    });

    this.bufferQueue.push(audioBuffer);
    this.queueDuration += audioBuffer.duration;

    if (!this.isPlaying) {
      this.play();
    }

    if (this.bufferQueue.length === 1) {
      this.playNext();
    }
  }

  play() {
    // if (this.queueDuration < 0.2) return;
    // this.nextPlayTime = this.audioCtx?.currentTime ?? 0.0;
    this.isPlaying = true;
    this.config.onStartPlaying?.();
    this.playNext();
  }

  stop(): void {
    const currentTime = this.audioCtx!.currentTime;

    const onPlaybackEnded = () => {
      console.log('player playback ended');
      this.activeSources.forEach((source) => source.stop());

      this.isPlaying = false;

      this.bufferQueue = [];
      this.nextPlayTime = 0.0;
      this.queueDuration = 0.0;
      this.setIsAllChunksRecieved(false);

      this.config.onPlaybackEnded?.();
    };

    if (this.isPlaybackEnded) {
      onPlaybackEnded();
    } else {
      // Устанавливаем плавное уменьшение громкости до 0
      this.gainNode.gain.setValueAtTime(this.gainNode.gain.value, currentTime); // Текущая громкость
      this.gainNode.gain.linearRampToValueAtTime(0, currentTime + FADEOUT_DURATION_SEC); // До 0 за FADEOUT_DURATION_SEC

      // Останавливаем все источники после завершения затухания
      setTimeout(onPlaybackEnded, FADEOUT_DURATION_SEC * 1000);
    }
  }

  clear() {
    this.bufferQueue = [];
  }

  get isPlaybackEnded() {
    return this.isAllChunksRecieved && !this.bufferQueue.length && !this.activeSources.length;
  }

  private playNext(): void {
    while (this.bufferQueue.length) {
      if (this.queueDuration < MIN_QUEUE_LENGTH) {
        console.warn('Voice queue is to low');
      }

      const nextBuf = this.bufferQueue.shift();
      if (nextBuf) {
        this.queueDuration -= nextBuf?.duration;
        const source = this.audioCtx!.createBufferSource();
        source.buffer = nextBuf;

        // Подключаем источник к GainNode
        source.connect(this.gainNode);

        // Сохраняем ссылку на активный источник
        this.activeSources.push(source);

        source.onended = () => {
          // Удаляем источник из списка активных при завершении
          const index = this.activeSources.indexOf(source);
          if (index !== -1) {
            this.activeSources.splice(index, 1);
          }

          if (this.isPlaybackEnded) {
            this.stop();
            return;
          }
        };

        if (this.audioCtx?.currentTime && this.nextPlayTime < this.audioCtx.currentTime) {
          this.nextPlayTime = this.audioCtx.currentTime;
          console.warn('Make next play time correction. Play may be inaccurate'); //in case of debug BP
        }

        this.gainNode.gain.setValueAtTime(1, this.audioCtx?.currentTime || 0);
        source.start(this.nextPlayTime);

        this.nextPlayTime += source.buffer.duration;
      }
    }
  }
}
